import React, { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import { Button, Heading, Types, RichText, baseVariables } from '@marriott/mi-ui-library';
import { ImageCarousel } from '../../../molecules';
import { useLocaleStore } from '../../../store';
import { useMediaQuery } from '../../../hooks';
import { isValidUrl } from '../../../utils';
import { EventBannerProps } from './EventBanner.types';
import { StyledEventBanner } from './EventBanner.styles';
import { isGroupType } from '../../../admin/utils/reslink';
import { ReservationLinkType } from '../../../admin/organisms/reslink/DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';

export const EventBanner: FC<EventBannerProps> = ({
  eventTitle,
  eventDescription,
  customLinks,
  externalLinkAriaLabel,
  isMapView = false,
  eventStartDate,
  eventEndDate,
  media,
  eventBannerLabels,
}) => {
  const { viewEventDetails, hideEventDetails, viewLessDetails, viewMoreDetails } = eventBannerLabels;

  const urlParams = useSearchParams();
  const reslinkType = (urlParams.get('key') as string) || '';
  const isGroup = isGroupType(reslinkType as ReservationLinkType);

  const isMobile = !useMediaQuery(baseVariables.mediaQuery.md);
  const isDesktop = useMediaQuery(baseVariables.mediaQuery.lg);
  const { rtl } = useLocaleStore();
  const [isExpandedMobile, setIsExpandedMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const sanitizedNode = DOMPurify.sanitize(eventDescription, { RETURN_DOM: true }) as HTMLElement;

  const fragmentString = sanitizedNode.innerHTML; // Get the HTML content of the container

  const toggleButton = () => {
    isMobile ? setIsExpandedMobile(!isExpandedMobile) : setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const checkOverflow = () => {
      const eventDescriptionElement = document.querySelector('.event-description');
      const pTagElements = eventDescriptionElement?.querySelectorAll('p');
      let childMargin = 0;
      pTagElements?.forEach(item => {
        const style = window.getComputedStyle(item);
        childMargin += parseFloat(style.marginTop) + parseFloat(style.marginBottom);
      });
      if (eventDescriptionElement && pTagElements?.[0]) {
        let hasOverflow =
          eventDescriptionElement.scrollHeight - childMargin > pTagElements[0].clientHeight ||
          eventDescriptionElement.scrollWidth > eventDescriptionElement.clientWidth;
        if (pTagElements.length === 1 && !hasOverflow && pTagElements[0].clientWidth !== pTagElements[0].scrollWidth) {
          hasOverflow = true;
        }
        setIsOverflowing(hasOverflow);
      } else {
        setIsOverflowing(false);
      }
    };

    if (isMapView) {
      if (isMobile) {
        setIsExpandedMobile(false);
        setIsExpanded(false);
        setIsOverflowing(false);
      } else {
        if (eventDescription && isEventDescriptionEmpty(eventDescription)) {
          checkOverflow();
        } else {
          setIsOverflowing(false);
        }
      }
    } else {
      setIsExpandedMobile(false);
      setIsExpanded(false);
      setIsOverflowing(false);
    }
  }, [isMapView, isMobile, eventDescription]);

  const imageCarouselElement = media ? (
    <div className="media-container">
      <ImageCarousel
        media={media}
        isCarouselIndicatorCentered
        containerClass="fullbleed-carousel"
        dir={rtl ? 'rtl' : 'ltr'}
      />
    </div>
  ) : null;

  const eventTitleElement = eventTitle ? (
    <Heading
      variation={Types.headingType.title}
      fontSize={Types.size.large}
      titleText={eventTitle}
      customClass={clsx('event-title')}
    />
  ) : null;

  const eventDateElement =
    isGroup && eventStartDate ? (
      <label className={clsx(' t-font-m mb-3', !isMobile && 't-label-s')}>
        {eventStartDate}
        {eventEndDate ? ` - ${eventEndDate}` : ''}
      </label>
    ) : null;

  const eventDescriptionElement = eventDescription ? (
    <>
      <RichText
        componentId={'eventDescription'}
        text={fragmentString}
        customClass={clsx(
          'custom-scrollbar event-description',
          isMapView && !isMobile && isExpanded && 'expanded',
          isOverflowing && !isMobile && !isExpanded && 'overflow'
        )}
      />
      {isMapView && !isMobile && isOverflowing && (
        <Button
          callback={toggleButton}
          isLink={false}
          buttonCopy={isExpanded ? viewLessDetails : viewMoreDetails}
          className="m-link-action"
        />
      )}
    </>
  ) : null;

  const customLinksElement = customLinks ? (
    <div className={clsx('custom-links', isMapView && !isMobile && 'justify-content-end')}>
      {customLinks.map((cta, index) => (
        <Button
          key={index}
          buttonCopy={cta.name}
          isLink
          className={clsx(isMapView ? 'm-button-secondary py-0' : 'm-button-external py-0', isDesktop && 'm-button-l')}
          href={isValidUrl(cta.link) ? cta.link : `//${cta.link}`}
          target={'_blank'}
          linkType={'external'}
          rel={'noopener noreferrer'}
          linkAriaLabelOpenNewWindow={externalLinkAriaLabel}
        />
      ))}
    </div>
  ) : null;

  const isEventDescriptionEmpty = (htmlString: string) => {
    if (typeof htmlString !== 'string') return false;

    const sanitizedNode = DOMPurify.sanitize(htmlString, { RETURN_DOM: true }) as HTMLElement;
    const textContent = sanitizedNode.textContent || sanitizedNode.innerText || '';
    return textContent.trim().length > 0;
  };

  const showEventButtonMobile =
    isEventDescriptionEmpty(eventDescription) || (Array.isArray(customLinks) && customLinks.length > 0);

  return (
    <StyledEventBanner className={clsx(media && 'with-media', isMapView && 'map-view-banner')}>
      {isMapView ? (
        !rtl ? (
          isMobile ? (
            <div className="event-banner" data-testid="event-banner">
              {eventDateElement}
              {eventTitleElement}
              {isExpandedMobile && (
                <>
                  {eventDescriptionElement}
                  {customLinksElement}
                </>
              )}
              {showEventButtonMobile && (
                <Button
                  callback={toggleButton}
                  isLink={false}
                  buttonCopy={isExpandedMobile ? hideEventDetails : viewEventDetails}
                  className={clsx('m-link-action', isExpandedMobile ? 'mt-2' : 'mt-4')}
                />
              )}
            </div>
          ) : (
            <>
              {imageCarouselElement}
              <div className="event-banner" data-testid="event-banner">
                <div>
                  {eventDateElement}
                  {eventTitleElement}
                  {eventDescriptionElement}
                </div>
                <div>{customLinksElement}</div>
              </div>
            </>
          )
        ) : null
      ) : (
        <>
          {imageCarouselElement}
          <div className="event-banner" data-testid="event-banner">
            {eventTitleElement}
            {eventDescriptionElement}
            {customLinksElement}
          </div>
        </>
      )}
    </StyledEventBanner>
  );
};
